import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components'; // Import styled-components
import { REMOTE_ENTRY_URI, MSAL_URI, CAF_URI } from '@config/envConfig';

// Define MFE Configuration Registry outside the component
const mfeConfigRegistry = {
  'psd-b2b': {
    remoteEntryUrl: REMOTE_ENTRY_URI,
    remoteName: 'PSDRemoteApp',
    exposedModule: './PSD_B2B_AppInjector',
    // Base config for injection options (token added later)
    baseInjectOptions: {
      type: 'b2b_steerco',
      themeMode: 'light',
      uri: MSAL_URI,
      redirectUrl: '/psd',
      accessTokenStorageKey: 'msalAccessToken' // Fixed storage key
    }
  },
  'ctx-profit-to-serve': {
    remoteEntryUrl: REMOTE_ENTRY_URI,
    remoteName: 'PSDRemoteApp',
    exposedModule: './PSDAppInjector',
    // Base config for injection options (token added later)
    baseInjectOptions: {
      type: 'ctx_profit_to_serve',
      themeMode: 'light',
      redirectUrl: '/psd',
      accessTokenStorageKey: 'msalAccessToken' // Fixed storage key
    }
  }
};

// Cache remote entry promises on the global window object.
const loadRemoteEntryOnce = (url, remoteName) => {
  if (!window.__remoteEntryPromises) {
    window.__remoteEntryPromises = {};
  }
  if (!window.__remoteEntryPromises[remoteName]) {
    window.__remoteEntryPromises[remoteName] = new Promise((resolve, reject) => {
      // If the remote container is already available, resolve immediately.
      if (window[remoteName]) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = url;
      script.type = 'text/javascript';
      script.async = true;
      script.onload = () => {
        console.log(`${remoteName} remote entry loaded.`);
        resolve();
      };
      script.onerror = () => reject(new Error(`Failed to load remote entry script: ${url}`));
      document.head.appendChild(script);
    });
  }
  return window.__remoteEntryPromises[remoteName];
};

const loadRemoteModule = async (remoteName, exposedModule) => {
  // Initialize the shared scope. These are webpack runtime functions.
  // Ensure __webpack_init_sharing__ and __webpack_share_scopes__ are available
  if (typeof __webpack_init_sharing__ === 'function' && typeof __webpack_share_scopes__ === 'object') {
    await __webpack_init_sharing__("default");
    const container = window[remoteName];
    if (!container || typeof container.init !== 'function' || typeof container.get !== 'function') {
      throw new Error(`Remote container ${remoteName} is not available or invalid.`);
    }
    // Initialize the container to provide shared modules.
    await container.init(__webpack_share_scopes__.default);
    // Get the module factory from the container.
    const factory = await container.get(exposedModule);
    const Module = factory();
    return Module;
  } else {
    throw new Error('Webpack sharing functions not available.');
  }
};

// Styled Components Definitions
const PageWrapper = styled.div`
  background-color: #eef2f7; /* Light background color */
  min-height: 100vh; /* Ensure it covers the full height */
  padding: 1px 0; /* Prevent margin collapse */
`;

const Card = styled.div`
  padding: 25px;
  margin: 20px; /* Consistent margin */
  border: 1px solid #d1d9e6;
  border-radius: 10px;
  /* Removed max-width, width will be determined by flex/grid or block behavior */
  background-color: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const StyledLabel = styled.label`
  display: block;
  margin-bottom: 8px; /* Slightly more space */
  font-weight: bold;
  color: #333;
`;

const baseInputStyle = `
  display: block;
  width: calc(100% - 20px); /* Adjust width accounting for padding/border */
  padding: 10px;
  margin-bottom: 18px; /* More space */
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 1rem;
`;

const StyledInput = styled.input`
  ${baseInputStyle}
`;

const StyledSelect = styled.select`
  ${baseInputStyle}
`;

const StyledButton = styled.button`
  padding: 12px 25px; /* Slightly larger */
  background-color: ${props => props.disabled ? '#b0bec5' : '#007bff'}; /* Adjusted disabled color */
  color: white;
  border: none;
  border-radius: 5px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 1rem;
  font-weight: 500;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: ${props => !props.disabled && '#0056b3'};
    box-shadow: ${props => !props.disabled && '0 4px 8px rgba(0, 0, 0, 0.15)'};
  }

  &:active {
      background-color: ${props => !props.disabled && '#004 C9F'};
      box-shadow: ${props => !props.disabled && 'inset 0 1px 3px rgba(0, 0, 0, 0.2)'};
  }
`;

const ErrorMessage = styled.div`
  color: #d32f2f; /* Darker red */
  margin-top: 15px;
  font-size: 0.9rem;
  font-weight: 500;
`;

const MfeContainer = styled.div`
  width: 100%;
  height: 100vh;
`;

const MfeTestPage = ({ initialState }) => {
  const containerId = 'remote-mfe-container';
  const [mfeIdentifierInput, setMfeIdentifierInput] = useState(initialState?.mfeIdentifierInput || ''); // Default to empty for placeholder
  const [accessTokenInput, setAccessTokenInput] = useState(initialState?.accessTokenInput || '');
  const [isLoading, setIsLoading] = useState(initialState?.isLoading || false);
  const [error, setError] = useState(initialState?.error || null);
  const [isMfeRendered, setIsMfeRendered] = useState(initialState?.isMfeRendered || false); // State to track MFE rendering
  const activeRemoteModuleRef = useRef(null);


  const unmountPreviousMfe = useCallback(() => {
    if (activeRemoteModuleRef.current && typeof activeRemoteModuleRef.current.unmount === 'function') {
      console.log('Unmounting previous MFE.');
      try {
        activeRemoteModuleRef.current.unmount(containerId);
      } catch (unmountError) {
        console.error("Error unmounting MFE:", unmountError);
      }
      activeRemoteModuleRef.current = null;
      // Don't reset isMfeRendered here, reset it when attempting to render again
    }
  }, [containerId]);

  const handleRenderClick = async () => {
    setIsMfeRendered(false); // Show card during loading/setup
    unmountPreviousMfe();
    setIsLoading(true);
    setError(null);

    if (!mfeIdentifierInput) {
      setError('Error: Please select an MFE Identifier.');
      setIsLoading(false);
      return;
    }

    const config = mfeConfigRegistry[mfeIdentifierInput];

    if (!config) {
      setError(`Error: MFE Identifier "${mfeIdentifierInput}" not found in registry.`);
      setIsLoading(false);
      return;
    }

    if (!accessTokenInput) {
      setError('Error: Access Token cannot be empty.');
      setIsLoading(false);
      return;
    }

    // Save the token to localStorage using the fixed key
    const storageKey = config.baseInjectOptions.accessTokenStorageKey;
      localStorage.setItem(storageKey, accessTokenInput);

    try {
      console.log(`Loading MFE: ${mfeIdentifierInput}`);
      await loadRemoteEntryOnce(config.remoteEntryUrl, config.remoteName);
      const remoteModule = await loadRemoteModule(config.remoteName, config.exposedModule);

      if (typeof remoteModule.inject !== 'function') {
        throw new Error(`The loaded module for ${mfeIdentifierInput} does not have an 'inject' function.`);
      }

      // Combine base options with the current access token
      const injectOptions = {
        ...config.baseInjectOptions,
        accessToken: accessTokenInput,
        refreshToken: null, // Or manage this if needed
        // Pass the accessTokenStorageKey from the config
      };

      console.log(`Injecting MFE with options:`, injectOptions);
      remoteModule.inject(containerId, injectOptions);
      activeRemoteModuleRef.current = remoteModule; // Store the active module for unmounting
      setIsMfeRendered(true); // Hide card on successful render
      console.log('MFE injected successfully.');

    } catch (loadError) {
      console.error('Failed to load or inject remote MFE:', loadError);
      setError(`Failed to load MFE: ${loadError.message}`);
      unmountPreviousMfe(); // Attempt cleanup on error
      // Keep isMfeRendered false so card stays visible showing the error
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      unmountPreviousMfe();
    };
  }, [unmountPreviousMfe]);

  return (
    <PageWrapper>
      {!isMfeRendered && (
        <Card>
          <h2>MFE Loader Configuration</h2>

          <StyledLabel htmlFor="mfeId">MFE Identifier:</StyledLabel>
          <StyledSelect
            id="mfeId"
            value={mfeIdentifierInput}
            onChange={(e) => setMfeIdentifierInput(e.target.value)}
            required // Added required attribute for basic HTML validation
          >
            <option value="" disabled>Select MFE</option>
            {Object.keys(mfeConfigRegistry).map(key => (
              <option key={key} value={key}>{key}</option>
            ))}
          </StyledSelect>

          <StyledLabel htmlFor="accessToken">Access Token:</StyledLabel>
              <StyledInput
                id="accessToken"
                type="text"
                value={accessTokenInput}
                onChange={(e) => setAccessTokenInput(e.target.value)}
                placeholder="Enter your access token"
                required
              />
          
          <StyledButton
            onClick={handleRenderClick}
            disabled={isLoading || !mfeIdentifierInput || !accessTokenInput} // Disable if loading or inputs empty
          >
            {isLoading ? 'Loading...' : 'Render MFE'}
          </StyledButton>

          {error && <ErrorMessage>{error}</ErrorMessage>}
        </Card>
      )}

      {/* Container where the MFE will be injected */}
      <MfeContainer id={containerId} />
    </PageWrapper>
  );
};

export default MfeTestPage;
